import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { GSIcon } from 'src/components/shared/GSIcon'
import { TableTabs } from 'src/components/shared/table/table-tabs/TableTabs'
import type { TableTabItem } from 'src/components/shared/table/table-tabs/TableTabs'
import { SchedulerTab } from 'src/constants/scheduler/schedulerTabs'
import { getRetryConfigsLoading, getRetryConfigsPagination } from 'src/selectors/retryConfig'
import { getRetryConfigTemplates } from 'src/selectors/retryConfigTemplate'
import { addS } from 'src/utils/baseUtils'

interface Props {
  selectedTab: SchedulerTab
  hasPartner: boolean
  onChange(tab: SchedulerTab): void
}

// Central icons paint via `currentColor`, so colour is set through the text colour, not `fill`.
const selectedIconStyles = tw`text-primary-600`
const iconStyles = tw`text-shades-1000`

/**
 * The two Schedulers Management tabs, the same shape as the customer list's Active / Inactive
 * tabs. Unlike those, the two tabs here are two different RESOURCES rather than two statuses of
 * one — the counts come from different places: the actions count is the server's `total`, the
 * templates count is the loaded array's length.
 */
const SchedulerTabsComponent = ({ selectedTab, hasPartner, onChange }: Props) => {
  const pagination = useSelector(getRetryConfigsPagination)
  const isListLoading = useSelector(getRetryConfigsLoading)
  const templates = useSelector(getRetryConfigTemplates)

  const tabs = useMemo(() => {
    const actionsTotal = pagination.total ?? 0
    // No count before a partner is chosen (the list cannot even be requested without one) and
    // none during the first load — a "0 actions" subtitle would read as an empty partner.
    const hasActionsCount = hasPartner && (!isListLoading || actionsTotal > 0)

    const items: Array<TableTabItem<SchedulerTab>> = [
      {
        value: SchedulerTab.ScheduledActions,
        title: 'Partners scheduled actions',
        subtitle: hasActionsCount ? `${actionsTotal} ${addS(actionsTotal, 'action')}` : '',
        renderIcon: (selected: boolean) => {
          return (
            <GSIcon
              iconName="gs_tasks"
              size={20}
              css={selected ? selectedIconStyles : iconStyles}
            />
          )
        },
      },
      {
        value: SchedulerTab.Templates,
        title: 'Retry config templates',
        subtitle: `${templates.length} ${addS(templates.length, 'template')}`,
        renderIcon: (selected: boolean) => {
          return (
            <GSIcon
              iconName="gs_settings_gear_1"
              size={20}
              css={selected ? selectedIconStyles : iconStyles}
            />
          )
        },
      },
    ]

    return items.map((item) => {
      return {
        ...item,
        dataTestId: `schedulers-tab-${item.value}`,
      }
    })
  }, [hasPartner, isListLoading, pagination.total, templates.length])

  return <TableTabs<SchedulerTab> tabs={tabs} selectedValue={selectedTab} onClick={onChange} />
}

export const SchedulerTabs = memo(SchedulerTabsComponent)
